import React, { useEffect, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import ErrorFetching from "../../../../components/modals/ErrorFetching";
import Pagination from "../../../../components/utils/Pagination";
import SearchBar from "./SearchBar";
import { HeaderObject, subEventObject } from "../../../../types/types";
import {
  getPageList,
  toggleKey,
  sortByKey,
  searchArrayByValue,
  getUniqueKeysForTable,
  insert,
  move,
  mapFromKeys,
} from "../../../../utils/functions";
import * as erState from "../../../../reducers/errorReducer";
import { useNotify } from "../../../../hooks/useNotify";
import { SubEventTableHeader } from "./SubEventHeader";
import { SubEventTableRow } from "./SubEventTableRow";
import Loading from "../../../../components/utils/Loading";

interface Props {
  tableList: subEventObject[];
  headerRow: string[];
  open: (id: string) => void;
  createSubEvent: () => void;
  loading: boolean;
}

export const SubEventTable: React.FC<Props> = ({
  tableList,
  open,
  headerRow,
  createSubEvent,
  loading,
}) => {
  const { message } = useSelector(erState.getErrorMessage);
  const errorMsg = useNotify(message);
  const filterDivToggleId = "SUBEVENTS_TABLE_FILTER";
  const tableSettingsDivId = "SUBEVENTS_TABLE_SETTINGS";
  const exportSettingsDivId = "SUBEVENTS_TABLE_EXPORT_SETTINGS";
  const pageLimit = 11;
  const initialState = {
    currentPage: 1,
    totalPages: Math.ceil(tableList.length / pageLimit),
    pageLimit: pageLimit,
    totalRecords: tableList.length,
    pageNeighbours: 0,
    filteredList: tableList,
    currentList: tableList.slice(0, pageLimit),
    filterKeys: [],
    searchWord: "",
    sortKey: headerRow[0],
    ascending: false,
    possibleSearchKeys: getUniqueKeysForTable(tableList),
    currentHeaders: headerRow,
    headers: {
      droppableColumnsSelected: headerRow,
      droppableColumnsAll: getUniqueKeysForTable(tableList).filter(
        (item) => !headerRow.includes(item)
      ),
    },
    export: {
      exportAll: {
        data: mapFromKeys(tableList, getUniqueKeysForTable(tableList)),
        filename: "subevents_all",
        label: "export all",
      },
      exportView: {
        data: mapFromKeys(tableList, headerRow),
        filename: "subevents_view",
        label: "export view",
      },
    },
  };

  const [state, setState] = useState(initialState);

  const onDragEnd = (dragObject: DropResult) => {
    if (dragObject.destination) {
      if (
        dragObject.source.droppableId === dragObject.destination.droppableId
      ) {
        const updated = move(
          state.headers[dragObject.destination.droppableId],
          dragObject.source.index,
          dragObject.destination.index
        );
        const updatedHeaders: HeaderObject = {
          ...state.headers,
          [dragObject.destination.droppableId]: updated,
        };
        const newExportedView =
          dragObject.destination.droppableId === "droppableColumnsSelected"
            ? mapFromKeys(state.filteredList, updated)
            : state.export.exportView.data;
        setState({
          ...state,
          headers: updatedHeaders,
          export: {
            ...state.export,
            exportView: {
              ...state.export.exportView,
              data: newExportedView,
            },
          },
        });
      } else {
        const updatedSource: string[] = state.headers[
          dragObject.source.droppableId
        ].filter((header) => header !== dragObject.draggableId);
        const updatedDestination: string[] = insert(
          state.headers[dragObject.destination.droppableId],
          dragObject.destination.index,
          dragObject.draggableId
        );
        const updatedHeaders: HeaderObject = ({
          [dragObject.source.droppableId]: updatedSource,
          [dragObject.destination.droppableId]: updatedDestination,
        } as unknown) as HeaderObject;
        const newExportedView = mapFromKeys(
          state.filteredList,
          updatedHeaders.droppableColumnsSelected
        );
        setState({
          ...state,
          headers: updatedHeaders,
          export: {
            ...state.export,
            exportView: {
              ...state.export.exportView,
              data: newExportedView,
            },
          },
        });
      }
    }
  };

  const gotoPage = (page: number) => {
    const currentList = getPageList(page, state.pageLimit, state.filteredList);
    const updated = {
      ...state,
      currentPage: page,
      currentList,
    };
    setState(updated);
  };

  const toggleFilter = (key: string) => {
    const updated = {
      ...state,
      filterKeys: toggleKey(key, state.filterKeys),
    };
    setState(updated);
  };

  const sort = (key: string) => {
    const sorted = sortByKey(key, state.ascending, state.filteredList);
    const newExportedView = mapFromKeys(
      sorted,
      state.headers.droppableColumnsSelected
    );

    const updated = {
      ...state,
      ascending: !state.ascending,
      sortKey: key,
      filteredList: sorted,
      currentList: sorted.slice(0, state.pageLimit),
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  const clearFilters = () => {
    const offset = (state.currentPage - 1) * state.pageLimit;
    const filteredList = searchArrayByValue(tableList, state.searchWord, []);
    const newExportedView = mapFromKeys(
      filteredList,
      state.headers.droppableColumnsSelected
    );
    const updated = {
      ...state,
      filteredList,
      currentList: filteredList.slice(offset, offset + state.pageLimit),
      totalPages: Math.ceil(filteredList.length / state.pageLimit),
      filterKeys: [],
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  const onSearchWordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const offset = (state.currentPage - 1) * state.pageLimit;
    const filteredList = searchArrayByValue(
      tableList,
      event.target.value,
      state.filterKeys
    );
    const newExportedView = mapFromKeys(
      filteredList,
      state.headers.droppableColumnsSelected
    );
    const updated = {
      ...state,
      searchWord: event.target.value,
      currentList: filteredList.slice(offset, offset + state.pageLimit),
      filteredList: filteredList,
      totalRecords: filteredList.length,
      totalPages: Math.ceil(filteredList.length / state.pageLimit),
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  useEffect(() => {
    setState({
      ...initialState,
      filteredList: tableList,
      totalRecords: tableList.length,
      currentList: tableList.slice(0, pageLimit),
      possibleSearchKeys: getUniqueKeysForTable(tableList),
      currentHeaders: headerRow,
      headers: {
        droppableColumnsSelected: headerRow,
        droppableColumnsAll: getUniqueKeysForTable(tableList).filter(
          (item) => !headerRow.includes(item)
        ),
      },
      export: {
        exportAll: {
          data: mapFromKeys(tableList, getUniqueKeysForTable(tableList)),
          filename: initialState.export.exportAll.filename,
          label: initialState.export.exportAll.label,
        },
        exportView: {
          data: mapFromKeys(tableList, headerRow),
          filename: initialState.export.exportView.filename,
          label: initialState.export.exportView.label,
        },
      },
    });
  }, [tableList]); //eslint-disable-line

  return (
    <div className='flex flex-col shadow-lg bg-brand-white md:shadow-lg my-5 md:w-full max-h-11/12 min-h-5/6 overflow-y-auto overflow-x-hidden scroller'>
      <ErrorFetching message={errorMsg} />
      <SearchBar
        exportSettings={state.export}
        sortKeys={state.possibleSearchKeys}
        onDragEnd={onDragEnd}
        selectedColumns={state.headers.droppableColumnsSelected}
        restColumns={state.headers.droppableColumnsAll}
        filterDivToggleId={filterDivToggleId}
        tableSettingsDivToggleId={tableSettingsDivId}
        exportSettingsDivToggleId={exportSettingsDivId}
        pageLimit={state.pageLimit}
        activeFilters={state.filterKeys}
        toggleFilter={toggleFilter}
        clearFilters={clearFilters}
        onSearchWordChange={onSearchWordChange}
        create={createSubEvent}
      />
      {loading ? (
        <Loading styles='my-auto' />
      ) : (
        <table className='md:bg-white rounded-lg md:table-fixed h-full w-full'>
          <SubEventTableHeader
            headers={state.headers.droppableColumnsSelected}
            sort={sort}
          />
          <tbody>
            {state.currentList.map((s, i) => (
              <SubEventTableRow
                subEvent={s}
                key={i}
                open={open}
                headerKeys={state.headers.droppableColumnsSelected}
              />
            ))}
          </tbody>
        </table>
      )}
      <Pagination
        pageLimit={state.pageLimit}
        totalPages={state.totalPages}
        pageNeighbours={state.pageNeighbours}
        currentPage={state.currentPage}
        onPageChanged={gotoPage}
      />
    </div>
  );
};

export default SubEventTable;
